/**
 * JWT Token Utilities for Frontend
 *
 * ⚠️ SECURITY IMPORTANT NOTES:
 *
 * 1. FRONTEND JWT PARSING IS FOR DATA EXTRACTION ONLY
 *    - We only decode the payload to extract user information
 *    - We NEVER validate the token signature on frontend
 *    - We NEVER make authentication decisions based on frontend parsing
 *
 * 2. BACKEND VALIDATION IS MANDATORY
 *    - All API requests must include the JWT token in Authorization header
 *    - Backend validates token signature, expiration, issuer, audience
 *    - Backend makes all authentication/authorization decisions
 *
 * 3. TOKEN SECURITY BEST PRACTICES
 *    - Store tokens in localStorage (for persistence) or memory (for security)
 *    - Never expose JWT secret key in frontend code
 *    - Use HTTPS in production
 *    - Implement token refresh mechanism
 *    - Clear tokens on logout
 *
 * 4. WHAT WE'RE DOING (SAFE):
 *    - Reading user data from JWT payload (public information)
 *    - Displaying user information in UI
 *    - Storing user preferences locally
 *
 * 5. WHAT WE'RE NOT DOING (UNSAFE):
 *    - Validating token signature
 *    - Making authentication decisions
 *    - Bypassing backend validation
 *    - Storing sensitive data in JWT
 */

export interface JwtUserData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  authType: string;
  isVerified: boolean;
  roles: string[];
  hasSetPassword?: boolean;
}

/**
 * Decodes JWT token to extract user data from the payload.
 *
 * ⚠️ SECURITY NOTE: This function is for data extraction ONLY.
 * JWT tokens should NEVER be validated on the frontend for authentication.
 * All authentication decisions must be made on the backend.
 *
 * @param token - The JWT token to decode
 * @returns User data from the token payload, or null if decoding fails
 */
export function decodeJwtToken(token: string): JwtUserData | null {
  try {
    // JWT tokens have 3 parts separated by dots
    const parts = token.split(".");
    if (parts.length !== 3) {
      console.error("Invalid JWT token format");
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];

    // Add padding if needed for base64 decoding
    const paddedPayload = payload + "=".repeat((4 - (payload.length % 4)) % 4);

    // Decode base64
    const decodedPayload = atob(
      paddedPayload.replace(/-/g, "+").replace(/_/g, "/")
    );

    // Parse JSON
    const claims = JSON.parse(decodedPayload);

    // Extract user data from claims
    const userData: JwtUserData = {
      id: claims.nameid || claims.sub || "",
      email: claims.email || "",
      firstName: claims.firstName || "",
      lastName: claims.lastName || "",
      authType: claims.authType || "",
      isVerified: claims.isVerified === "True",
      roles: claims.roles ? JSON.parse(claims.roles) : [],
      hasSetPassword:
        claims.hasSetPassword === "true" || claims.hasSetPassword === true,
    };

    return userData;
  } catch (error) {
    console.error("Error decoding JWT token:", error);
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return true;

    const payload = parts[1];
    const paddedPayload = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    const decodedPayload = atob(
      paddedPayload.replace(/-/g, "+").replace(/_/g, "/")
    );
    const claims = JSON.parse(decodedPayload);

    // Check if token is expired
    const exp = claims.exp;
    if (!exp) return true;

    const currentTime = Math.floor(Date.now() / 1000);
    return currentTime >= exp;
  } catch (error) {
    console.error("Error checking token expiration:", error);
    return true;
  }
}
